import { defineComponent as _defineComponent } from 'vue'

import { defineComponent } from '@nuxtjs/composition-api'

const __default__ = defineComponent({
  middleware: ['ensureUserAuth', 'forceRecruiterToChargebeeCheckout'],
})

import { computed, onMounted, ref, useRoute, useRouter, watch } from '@nuxtjs/composition-api'
import { useFetcher } from '~/store/useFetcher'
import { useUserStore } from '~/store/userStore'
import { GetResponseContent } from '~/lib/app/apiTypes'
import { showError } from '~/store/notificationStore'
import { useStringSearchParameter } from '~/lib/composables/useSearchParameter'
import { Discoverability, TalentProfileStatuses } from '~/lib/talentProfileEnums'
import { useLayoutInfo } from '~/lib/composables/useLayoutInfo'
import { useElementSize, useLocalStorage } from '@vueuse/core'
import mixpanel from 'mixpanel-browser'
import { Temporal } from '@js-temporal/polyfill'
import { SessionStorageKey } from '~/lib/app/sessionStorage'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'default',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const fetcher = useFetcher()
const user = useUserStore()

const routeName = computed(() => route.value.name?.split('__')[0])

const mobileMenuOpen = ref(false)
watch(
  () => route.value,
  () => {
    mobileMenuOpen.value = false
  },
)

const action = useStringSearchParameter('action')
const [userImpersonationModalOpen, companyImpersonationModalOpen] = [
  'impersonate-user',
  'impersonate-company',
].map((name) => {
  return computed({
    get: () => action.value === name,
    set: (v) => {
      action.value = v ? name : undefined
    },
  })
})

const feedbackModalOpen = computed(() => action.value === 'feedback')

function closeFeedbackModal() {
  action.value = undefined
}

onMounted(() => {
  if (!user.isCompany) return

  if (!user.company.isOnboardingComplete) {
    router.push('/company/onboarding')
  }

  if (!user.company.firsJobDone && route.value.path != '/edit-position') {
    router.push('/company/positions')
  }
})

const hyreApprovedDialogOpen = ref(false)
const onboardingInfo = ref<GetResponseContent<`/api/v2/talent/{talentId}/onboarding`>>()

onMounted(checkAndShowTalentModals)

async function checkAndShowTalentModals() {
  if (!user.isUserLoggedIn || !user.isTalent) {
    return
  }

  onboardingInfo.value = await fetcher.$get(
    `/api/v2/talent/${user.externalUserId}/onboarding` as `/api/v2/talent/{talentId}/onboarding`,
  )

  const onboarding = onboardingInfo.value!

  const doShowHyreApprovedModall =
    onboarding.profileApprovalStatus === TalentProfileStatuses.Approved &&
    !onboarding.checklist?.didViewApprove

  if (doShowHyreApprovedModall) {
    hyreApprovedDialogOpen.value = true
    await fetcher.$put(`/api/v2/talent/onboarding`, { didViewApprove: true })
    return
  }

  const daysSinceSignup = getDaysSinceSignup(onboarding!)

  const doShowWelcomeBackModal =
    !onboarding.checklist?.didCompleteBasicInfo &&
    (onboarding.profileApprovalStatus === 'Approved' ||
      (onboarding.profileApprovalStatus === 'Draft' && daysSinceSignup > 10))

  if (doShowWelcomeBackModal) {
    showWelcomeBackDialog.value = true
    return
  }

  // when they have been in the onboarding wizard this session, we shouldn't show them the get started modal.
  // This will probably change as we at some point will want to start forcing them to complete the onboarding before we allow them to do anything else
  const hasBeenInOnboarding =
    sessionStorage.getItem(SessionStorageKey.HasBeenInOnboarding) === 'true'

  const doShowGetStartedDialog =
    !onboarding.checklist?.didCompleteBasicInfo &&
    onboarding.profileApprovalStatus === 'Draft' &&
    !hasBeenInOnboarding

  if (doShowGetStartedDialog) {
    showGetStartedDialog.value = true
    return
  }

  const { show: showTm, numJobs } = await shouldShowTinderMatchingNotification()
  if (showTm) {
    tinderMatchingNotificationModalData.value = {
      numMatches: numJobs,
    }

    tmNotificationViewedTimestamp.value = { timestamp: Date.now() }

    mixpanel.track('Self Reactivation Popup Viewed', {
      NumMatches: tinderMatchingNotificationModalData.value.numMatches,
    })

    return
  }
}

function getDaysSinceSignup(info: { signupDate?: string | null }) {
  const signupDate = Temporal.Instant.from(info.signupDate!)
  return Temporal.Now.instant().since(signupDate).total('days')
}

async function shouldShowTinderMatchingNotification(): Promise<{ show: boolean; numJobs: number }> {
  if (!onboardingInfo.value?.checklist?.didCompleteBasicInfo) {
    return { show: false, numJobs: 0 }
  }

  if (onboardingInfo.value?.profileApprovalStatus === 'Rejected') {
    return { show: false, numJobs: 0 }
  }

  const now = Date.now()
  const lastViewed = tmNotificationViewedTimestamp.value.timestamp
  const oneWeekInMs = 7 * 24 * 60 * 60 * 1000
  if (now - lastViewed < oneWeekInMs) {
    return { show: false, numJobs: 0 }
  }

  const jobs = await fetcher.$get(
    `/api/v2/talent/${user.externalUserId}/jobs` as `/api/v2/talent/{talentId}/jobs`,
    { query: { matchesOnly: 'true' } },
  )

  if (jobs.length === 0) {
    return { show: false, numJobs: 0 }
  }

  return {
    show: true,
    numJobs: jobs.length,
  }
}

const isHotlistStatusSelectorPopupOpen = ref(false)
const isHotlistStatusUpdating = ref(false)
const isHotlistStatusActive = computed(() => user.isTalent && user.talent?.isHotlistActive)

async function changeHotlistStatus(newStatus: boolean) {
  try {
    isHotlistStatusSelectorPopupOpen.value = false
    isHotlistStatusUpdating.value = true

    await fetcher.$post('/api/v2/talent/hotlist-status', {
      status: newStatus ? Discoverability.Yes : Discoverability.No,
    })

    await user.refreshData()
  } catch (e) {
    showError('Error occured while updating status')
  } finally {
    isHotlistStatusUpdating.value = false
  }
}

const { isMobileLayout } = useLayoutInfo()

const sidebar = ref<HTMLElement>()
const { width: sidebarWidth } = useElementSize(sidebar)

const impersonateRunning = ref(false)

async function impersonateTalent(talentGuid: string) {
  if (impersonateRunning.value) return
  impersonateRunning.value = true

  try {
    await user.impersonate({ talentGuid })
    userImpersonationModalOpen.value = false
  } finally {
    impersonateRunning.value = false
  }
}

async function impersonateCompany(companyGuid: string) {
  if (impersonateRunning.value) return
  impersonateRunning.value = true

  try {
    await user.impersonate({ companyGuid })
    companyImpersonationModalOpen.value = false
  } finally {
    impersonateRunning.value = false
  }
}

const tmNotificationViewedTimestamp = useLocalStorage('talentTmNotification.timestamp', {
  timestamp: 0,
})

const tinderMatchingNotificationModalData = ref<{
  numMatches: number
}>()

function onTinderMatchingNotificationConfirmClicked() {
  const data = tinderMatchingNotificationModalData.value!
  mixpanel.track('Self Reactivation Popup CTA Clicked', { NumMatches: data.numMatches })
  router.push({ path: '/matching', query: { trig: 'selfReactivationPopup' } })
}

const showWelcomeBackDialog = ref(false)
const showGetStartedDialog = ref(false)

const needsToAgreeToCompanyHandover = ref(false)
onMounted(async () => {
  await user.refreshData()
  if (user.talent == null || user.isImpersonating) return

  const didSignupBefore2025 =
    Temporal.PlainDate.compare(user.talent.signupDate, Temporal.PlainDate.from('2025-01-01')) < 0

  if (didSignupBefore2025 && !user.talent.didAgreeToCompanyChange) {
    needsToAgreeToCompanyHandover.value = true
  }
})

async function agreeToCompanyHandover() {
  user.talent!.setDidAgreeToCompanyChange()
  await fetcher.$post(
    `/api/v2/talent/${user.externalUserId}/accept-company-handover` as `/api/v2/talent/{talentId}/accept-company-handover`,
    {},
  )

  needsToAgreeToCompanyHandover.value = false
}

return { __sfc: true,route, router, fetcher, user, routeName, mobileMenuOpen, action, userImpersonationModalOpen, companyImpersonationModalOpen, feedbackModalOpen, closeFeedbackModal, hyreApprovedDialogOpen, onboardingInfo, checkAndShowTalentModals, getDaysSinceSignup, shouldShowTinderMatchingNotification, isHotlistStatusSelectorPopupOpen, isHotlistStatusUpdating, isHotlistStatusActive, changeHotlistStatus, isMobileLayout, sidebar, sidebarWidth, impersonateRunning, impersonateTalent, impersonateCompany, tmNotificationViewedTimestamp, tinderMatchingNotificationModalData, onTinderMatchingNotificationConfirmClicked, showWelcomeBackDialog, showGetStartedDialog, needsToAgreeToCompanyHandover, agreeToCompanyHandover }
}

})